import { Component, Input } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';

import { MatterService } from '../../core/services/matter.service';
import { ClientDocument, ClientDocumentHelper, DocumentChildrenMap } from '../../core/models/client-document';
import { ClientDocumentService } from '../../core/services/client-document.service';
import { DocumentDetailsDialogComponent } from '../document-details-dialog/document-details-dialog.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-document-list',
  templateUrl: './document-list.component.html',
  styleUrl: './document-list.component.scss'
})
export class DocumentListComponent {
  DateTime = DateTime;

  @Input() matterId: string | undefined;

  documents: ClientDocument[] = [];
  defaultColumns: string[] = ['description', 'dueDate', 'uploadStatus', 'link'];
  dataSource = new MatTableDataSource(this.documents);
  displayedColumns = this.defaultColumns;
  loading = true;
  addItemText = DocumentListComponent.getAddItemText(false);

  constructor(private clientDocumentService: ClientDocumentService, private matterService: MatterService, private dialog: MatDialog, private bpObserver: BreakpointObserver) {

    this.bpObserver.observe(Breakpoints.XSmall).subscribe(result => {
      this.addItemText = DocumentListComponent.getAddItemText(result.matches);
    });
  }

  ngOnInit(): void {
    this.getDocuments();
  }

  getDocuments() {
    const observable = this.matterId == null ? this.clientDocumentService.getAll() : this.matterService.getClientDocuments(this.matterId);

    this.loading = true;
    this.documents = [];

    observable.subscribe({
      next: (documents: ClientDocument[]) => {
        this.documents = documents;

        const sortedDocuments = this.getSortedDocuments();

        this.dataSource = new MatTableDataSource(sortedDocuments);

        this.loading = false;
      },
      error: (error: any) => this.loading = false
    });
  }

  getSortedDocuments(): ClientDocument[] {
    return this.documents.filter(d => d.parentId == null)
      .sort((a, b) => {
        if (a.requestedByAgent) {
          return b.requestedByAgent ? a.displayOrder - b.displayOrder : -1;
        }

        return b.requestedByAgent ? 1 : 0;
      });

  }

  getDueDateClass(element: ClientDocument): string {
    const expiredClass = "expired";
    const expiringClass = "expiring";

    // Warn if the document is due in the next 7 days
    const warningDays = 7;

    if (element.completed) {
      return "";
    }

    var dueDate = DateTime.fromISO(element.dueDate!);
    if (dueDate.toUnixInteger() < DateTime.now().toUnixInteger()) {
      return expiredClass;
    }
    if (dueDate.diffNow('days').days < warningDays) {
      return expiringClass;
    }
    return "";
  }

  getStatusIcon(document: ClientDocument) {
    const status = this.getStatus(document);

    if (status === 'other') {
      return 'send';
    } else if (status === 'complete') {
      return 'check_circle';
    } else if (status === 'incomplete') {
      return 'cloud_upload'; // ???
    } else if (status === 'rejected') {
      return 'error_outline';
    }

    return null;
  }

  getStatusClass(document: ClientDocument) {
    const status = this.getStatus(document);

    if (status === 'complete') {
      return 'complete';
    } else if (status === 'rejected') {
      return 'error';
    } else if (status === 'other') {
      return 'send';
    } else if (status === 'incomplete') {
      return 'incomplete';
    }

    return null;
  }

  getStatus(document: ClientDocument) {
    return ClientDocumentHelper.getStatus(document);
  }

  addItem() {
    const dialogRef = this.dialog.open(DocumentDetailsDialogComponent, {
      data: {
        document: null,
        documentSelection: this.getSortedDocuments(),
        matterId: this.matterId
      },
      panelClass: 'dialog-panel-large'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getDocuments(); // Refresh the document list
    });
  }

  viewItem(item: ClientDocument) {
    const dialogRef = this.dialog.open(DocumentDetailsDialogComponent, {
      data: {
        document: item,
        documents: this.documents,
        documentSelection: [],
        matterId: this.matterId
      },
      panelClass: 'dialog-panel-large',
      autoFocus: 'dialog'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.getDocuments(); // Refresh the document list
    });
  }

  static getAddItemText(abbreviate: boolean): string {
    return abbreviate ? 'Add' : 'Add Item';
  }
}
